import type { KeyedMutator } from 'swr';
import type { ActivityDTO } from 'src/types/dto';
import { ActivityStatus, PRE_REQUESTED_ONWARDS } from 'src/types/dto';

import dayjs from 'dayjs';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { Box, Grid, Stack, Button, Divider, Typography, IconButton, Tooltip } from '@mui/material';

import { paths } from 'src/routes/paths';

import { formatActivityDurationDaysLabel } from 'src/utils/duration';
import { getCompletionWindow } from 'src/utils/format-time';

import { Priority } from 'src/components/priority';
import { TagsList } from 'src/components/tags-list';
import { IconLabel } from 'src/components/IconLabel';
import { Iconify } from 'src/components/iconify';
import { getCompetencyName } from 'src/utils/competency';

import { ActivityDependencyTags } from './activity-dependency-tags';

import { ActivityTeamTable } from './activity-team-table';
import { StatusDropdown } from 'src/components/status-dropdown';
import { ConfirmDialog } from 'src/components/custom-dialog';
import axios, { endpoints } from 'src/utils/axios';
import { toast } from 'src/components/snackbar';
import { mapBookingRequestError } from 'src/utils/booking-request-errors';
import {
  isBookingRequestTriggerStatus,
  withBookingRequestTrigger,
} from 'src/utils/booking-request-trigger';

export function ActivityDetails({
  visit,
  mutateVisit,
  canEditCompetencies = false,
  onEditCompetencies,
}: {
  visit: ActivityDTO;
  mutateVisit: KeyedMutator<ActivityDTO>;
  canEditCompetencies?: boolean;
  onEditCompetencies?: () => void;
}) {
  const { t } = useTranslation('activities');
  const router = useRouter();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);

  const performStatusChange = async (status: ActivityStatus, confirmCancellation?: boolean) => {
    setIsUpdating(true);
    try {
      // Picking "Requested" / "Pre requested" here starts the booking-request workflow, so
      // it must take the same locked sync-trigger path as the action-panel Request button.
      await axios.patch(
        endpoints.activities.status,
        withBookingRequestTrigger({
          ids: [visit.id],
          status,
          ...(confirmCancellation && { confirmCancellation }),
        })
      );
      const isBookingRequestTrigger = isBookingRequestTriggerStatus(status);
      if (isBookingRequestTrigger) {
        toast.success(t('actionPanel.preRequestOrRequestPending'));
      }
      // Reflect the change right away; the refetch in `finally` confirms it. A booking-request
      // trigger keeps the current status and only sets the in-flight marker on the backend.
      const optimistic: Partial<ActivityDTO> = isBookingRequestTrigger
        ? { bookingRequestInProgress: true }
        : { status };
      await mutateVisit((current) => (current ? { ...current, ...optimistic } : current), {
        revalidate: false,
      });
    } catch (error) {
      console.error('Error updating activity status:', error);
      if ((error as { message?: string })?.message === 'PROJECT_ROLE_REQUIRED') {
        toast.error(t('common:feedbackCodes.PROJECT_ROLE_REQUIRED'));
        return;
      }
      if ((error as { message?: string })?.message === 'ALLOCATED_AUDITOR_REQUIRED') {
        toast.error(t('common:feedbackCodes.ALLOCATED_AUDITOR_REQUIRED'));
        return;
      }
      const bookingRequestFeedback = mapBookingRequestError(error);
      if (bookingRequestFeedback) {
        toast.error(t(bookingRequestFeedback.key, bookingRequestFeedback.values));
        return;
      }
      toast.error(t('activityModal.actions.updateStatusError'));
    } finally {
      // Keep the dropdown disabled until the refetch lands so it never offers transitions
      // based on a stale status (also covers changes that applied despite an error). A failed
      // refetch must never leave the dropdown stuck disabled.
      try {
        await mutateVisit();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const handleStatusChange = async (status: ActivityStatus) => {
    if (status === ActivityStatus.CANCELLED) {
      setIsFetchingStatus(true);
      try {
        const response = await axios.get<ActivityDTO>(
          endpoints.activities.details(visit.id.toString())
        );
        const latestStatus = response.data.status as ActivityStatus;

        if (PRE_REQUESTED_ONWARDS.includes(latestStatus)) {
          setConfirmCancel(true);
          return;
        }
      } catch (error) {
        console.error('Error fetching latest activity status:', error);
        if (PRE_REQUESTED_ONWARDS.includes(visit.status as ActivityStatus)) {
          setConfirmCancel(true);
          return;
        }
      } finally {
        setIsFetchingStatus(false);
      }
    }
    performStatusChange(status);
  };
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.basic.status')}
            icon="ic:baseline-radio-button-checked"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <StatusDropdown
            status={visit.status as ActivityStatus}
            onChange={handleStatusChange}
            disabled={isFetchingStatus || isUpdating}
            activityProjectOwnerId={visit.standard?.project?.owner?.id}
            bookingRequestInProgress={visit.bookingRequestInProgress}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.basic.duration')}
            icon="ic:baseline-hourglass-empty"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Typography variant="body2">{formatActivityDurationDaysLabel(visit)}</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.basic.priority')}
            icon="solar:flag-bold"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Priority priority={visit.priority as 'NORMAL' | 'HIGH' | 'LOW'} />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.basic.type')}
            icon="ic:outline-layers"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          <Typography variant="body2">{visit.type}</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.basic.description')}
            icon="ic:outline-subject"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {visit.description?.trim() ? visit.description : '—'}
          </Typography>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h6">
          {t('activityModal.details.sections.dependencies.title')}
        </Typography>
        <Divider />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.dependencies.dependsOn')}
            icon="ic:outline-link"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <ActivityDependencyTags
            dependencies={visit.dependsOnActivities ?? []}
            containerId={`depends-on-container-${visit.id}`}
            t={t}
            emptyLabel={t('activityModal.details.sections.dependencies.dependsOnEmpty')}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.dependencies.requiredBy')}
            icon="ic:outline-subdirectory-arrow-right"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <ActivityDependencyTags
            dependencies={visit.dependentActivities ?? []}
            containerId={`required-by-container-${visit.id}`}
            t={t}
            emptyLabel={t('activityModal.details.sections.dependencies.requiredByEmpty')}
          />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="h6">
          {t('activityModal.details.sections.requirements.title')}
        </Typography>
        <Divider />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.requirements.window')}
            icon="ic:outline-date-range"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="body2">{getCompletionWindow(visit)}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.requirements.target')}
            icon="ic:baseline-bookmark-border"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="body2">
            {visit.targetDate ? dayjs(visit.targetDate).format('DD MMM YY') : ''}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.requirements.competencies')}
            icon="ic:outline-label"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={9}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Box id={`competencies-container-${visit.id}`} sx={{ minWidth: 0, flex: 1 }}>
              {visit.activityCompetencies && (
                <TagsList
                  containerIdentifier={`competencies-container-${visit.id}`}
                  tags={visit.activityCompetencies
                    .filter((entry) => entry?.competency)
                    .map((entry) => ({
                      id: entry.competency.id,
                      label: getCompetencyName(entry.competency),
                      color: 'info',
                      variant: 'filled',
                    }))}
                  color="info"
                  variant="filled"
                  showTooltip
                />
              )}
            </Box>
            {canEditCompetencies && onEditCompetencies && (
              <Tooltip title={t('activityModal.details.sections.requirements.editCompetencies')}>
                <IconButton
                  size="small"
                  onClick={onEditCompetencies}
                  aria-label={t('activityModal.details.sections.requirements.editCompetencies')}
                  sx={{ flexShrink: 0 }}
                >
                  <Iconify icon="solar:pen-bold" width={18} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* <Box>
        <Typography variant="h6">{t('activityModal.details.sections.scheme.title')}</Typography>
        <Divider />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel label={t('activityModal.details.sections.scheme.name')} icon="ic:outline-stars" fontSize={16} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="body2">{visit.standard?.name ?? '—'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel label={t('activityModal.details.sections.scheme.code')} icon="ic:baseline-abc" fontSize={16} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="body2">{visit.standard?.code ?? '—'}</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel label={t('activityModal.details.sections.scheme.standard')} icon="ic:outline-shield" fontSize={16} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="body2">{visit.standard?.standardTemplate?.category?.name ?? '—'}</Typography>
        </Grid>
      </Grid> */}
      <Box>
        <Typography variant="h6">{t('activityModal.details.sections.customer.title')}</Typography>
        <Divider />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.customer.name')}
            icon="ic:outline-account-balance"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            component="a"
            variant="body2"
            target="_blank"
            onClick={() =>
              router.push(paths.dashboard.client.details(visit.customer?.id.toString() ?? ''))
            }
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {`${visit.customer?.externalId ? `${visit.customer?.externalId} - ` : ''}${visit.customer?.name ?? visit.customer?.id}`}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.customer.site')}
            icon="ic:outline-location-on"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {visit.site?.address ?? '—'}
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <IconLabel
            label={t('activityModal.details.sections.customer.project')}
            icon="ic:outline-folder"
            fontSize={16}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            component="a"
            variant="body2"
            target="_blank"
            onClick={() =>
              router.push(
                paths.dashboard.projects.details(visit.standard?.project?.id.toString() ?? '')
              )
            }
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {visit.standard?.project?.orderReference ?? visit.standard?.project?.id}
          </Typography>
        </Grid>
      </Grid>
      <Box>
        <Typography variant="h6">{t('activityModal.details.sections.team.title')}</Typography>
        <Divider />
      </Box>
      <ActivityTeamTable activity={visit} />
      <ConfirmDialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        title={t('actionPanel.confirmCancelTitle')}
        content={t('actionPanel.confirmCancelContent')}
        closeLabel={t('actionPanel.confirmCancelNo')}
        action={
          <Button
            variant="contained"
            color="error"
            disabled={isUpdating}
            onClick={() => {
              setConfirmCancel(false);
              performStatusChange(ActivityStatus.CANCELLED, true);
            }}
          >
            {t('actionPanel.confirmCancelYes')}
          </Button>
        }
      />
    </Stack>
  );
}
